export type OccasionTheme =
  | "default"
  | "diwali"
  | "caelius-classic"
  | "holi"
  | "independence-day"
  | "christmas"
  | "birthday"
  | "monsoon"
  | "spring"
  | "anniversary";

export interface ThemeDefinition {
  id: OccasionTheme;
  name: string;
  description: string;
  emoji: string;
  /** Month-day ranges for auto-activation (inclusive). Omit for manual-only themes. */
  autoActivate?: { from: string; to: string }[];
}

export const occasionThemes: ThemeDefinition[] = [
  {
    id: "default",
    name: "Caelius Classic",
    description: "The signature Caelius One look — professional blue",
    emoji: "💎",
  },
  {
    id: "caelius-classic",
    name: "Caelius",
    description: "The signature Caelius One look — professional blue",
    emoji: "💎",
  },
  {
    id: "diwali",
    name: "Diwali",
    description: "Festival of lights — warm amber & gold",
    emoji: "🪔",
    autoActivate: [{ from: "10-20", to: "11-05" }],
  },
  {
    id: "holi",
    name: "Holi",
    description: "Festival of colors — vibrant pink, purple & cyan",
    emoji: "🎨",
    autoActivate: [{ from: "03-10", to: "03-20" }],
  },
  {
    id: "independence-day",
    name: "Independence Day",
    description: "Saffron, white & green tricolor pride",
    emoji: "🇮🇳",
    autoActivate: [{ from: "08-13", to: "08-17" }],
  },
  {
    id: "christmas",
    name: "Christmas & New Year",
    description: "Festive red & emerald with holiday cheer",
    emoji: "🎄",
    autoActivate: [{ from: "12-22", to: "01-03" }],
  },
  {
    id: "birthday",
    name: "Birthday Celebration",
    description: "Your special day — pink confetti & warm glow",
    emoji: "🎂",
  },
  {
    id: "monsoon",
    name: "Monsoon",
    description: "Deep ocean blues & refreshing teals",
    emoji: "🌧️",
    autoActivate: [{ from: "07-01", to: "09-15" }],
  },
  {
    id: "spring",
    name: "Spring",
    description: "Fresh greens & warm sunshine",
    emoji: "🌱",
    autoActivate: [{ from: "02-15", to: "04-15" }],
  },
  {
    id: "anniversary",
    name: "Company Anniversary",
    description: "Celebrating Caelius — purple & gold",
    emoji: "🏆",
  },
];

/**
 * Determine which occasion theme should be active based on the current date.
 * Returns "default" if no occasion matches.
 * Birthday theme is handled separately (requires user data).
 */
export function getAutoTheme(date: Date = new Date()): OccasionTheme {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const mmdd = `${month}-${day}`;

  for (const theme of occasionThemes) {
    if (!theme.autoActivate) continue;

    for (const range of theme.autoActivate) {
      if (isDateInRange(mmdd, range.from, range.to)) {
        return theme.id;
      }
    }
  }

  return "default";
}

function isDateInRange(mmdd: string, from: string, to: string): boolean {
  if (from <= to) {
    return mmdd >= from && mmdd <= to;
  }
  // Wraps around year boundary (e.g., Dec 22 → Jan 3)
  return mmdd >= from || mmdd <= to;
}

export function getThemeById(id: OccasionTheme): ThemeDefinition {
  return occasionThemes.find((t) => t.id === id) ?? occasionThemes[0];
}
